import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ShowChart as ShowChartIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import qcService, {
  QcChartFilters,
  QcChartPoint,
  QcChartSummary,
} from '../../services/qcService';
import LeveyJenningsChart from './LeveyJenningsChart';
import BlankChart from './BlankChart';
import RpdChart from './RpdChart';
import WestgardViolationsPanel from './WestgardViolationsPanel';

const CONTROL_TYPES = [
  { value: 'CRM_RECOVERY', label: 'MRC' },
  { value: 'BLANK', label: 'Blancs analytiques' },
];

const prettyChartLabel = (c: QcChartSummary): string => {
  if (c.control_type === 'PREP_CONCASSAGE_BLANK') return 'Blanc Concassage (préparation)';
  if (c.control_type === 'PREP_BROYAGE_BLANK') return 'Blanc Broyage (préparation)';
  if (c.control_type === 'PREP_CONCASSAGE_QUALITY') return 'Qualité de concassage';
  if (c.control_type === 'PREP_BROYAGE_QUALITY') return 'Qualité de broyage';
  if (c.control_type === 'DUPLICATE_RPD') return `${c.element} — RPD`;
  if (c.control_type === 'BLANK') return `${c.element} — Blanc`;
  return `${c.element} — MRC ${c.reference_material || ''}`;
};

const prettyLab = (lab: string): string =>
  lab === 'PREP_MECANIQUE'
    ? 'Préparation mécanique'
    : lab.replace('LABORATOIRE_', '');

/**
 * Client-portal QA/QC tab.
 * Backend filters every query by the caller's own client_id, so we only need
 * UI controls for laboratory / element / control type / date range.
 */
export const ClientQcTab: React.FC = () => {
  // Default window: last 90 days (mirrors backend default).
  const defaultFrom = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  })();

  const [filters, setFilters] = useState<QcChartFilters>({ from: defaultFrom });
  const [charts, setCharts] = useState<QcChartSummary[]>([]);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [selectedChartId, setSelectedChartId] = useState<string | null>(null);
  const [points, setPoints] = useState<QcChartPoint[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [refMaterial, setRefMaterial] = useState<string>('');
  const printRef = useRef<HTMLDivElement | null>(null);

  const loadCharts = useCallback(async () => {
    setLoadingCharts(true);
    setError(null);
    try {
      const list = await qcService.listClientCharts(filters);
      setCharts(list);
      if (list.length > 0 && !list.find((c) => c.chart_id === selectedChartId)) {
        setSelectedChartId(list[0].chart_id);
      }
      if (list.length === 0) setSelectedChartId(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoadingCharts(false);
    }
  }, [filters, selectedChartId]);

  const loadDetail = useCallback(async (chartId: string) => {
    setLoadingDetail(true);
    try {
      const d = await qcService.getClientChartDetail(chartId, {
        from: filters.from,
        to: filters.to,
      });
      setPoints(d.points || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement de la carte');
    } finally {
      setLoadingDetail(false);
    }
  }, [filters.from, filters.to]);

  useEffect(() => {
    loadCharts();
  }, [loadCharts]);

  useEffect(() => {
    if (selectedChartId) loadDetail(selectedChartId);
  }, [selectedChartId, loadDetail]);

  const selectedChart = useMemo(
    () => charts.find((c) => c.chart_id === selectedChartId) || null,
    [charts, selectedChartId],
  );

  // The OREAS / MRC reference-material filter only makes sense for MRC charts.
  // It stays available when no control type is chosen ("Tous types", which can
  // include MRC charts) or when MRC is explicitly selected; for any other
  // control type there are no reference materials, so the filter is disabled.
  const oreasApplicable =
    !filters.control_type || filters.control_type === 'CRM_RECOVERY';

  // OREAS / MRC reference materials present in the current chart set.
  const referenceMaterials = useMemo(
    () =>
      Array.from(
        new Set(charts.map((c) => c.reference_material).filter((r): r is string => !!r)),
      ).sort(),
    [charts],
  );

  // The effective filter value — guard against a stale selection that is no
  // longer present (prevents MUI "out-of-range value" warnings and a broken
  // Select when the chart set changes).
  const effectiveRefMaterial =
    oreasApplicable && referenceMaterials.includes(refMaterial) ? refMaterial : '';

  // Reset the OREAS filter whenever it stops being applicable or its value
  // disappears from the current chart set.
  useEffect(() => {
    if (refMaterial && refMaterial !== effectiveRefMaterial) {
      setRefMaterial('');
    }
  }, [refMaterial, effectiveRefMaterial]);

  // Apply the client-side reference-material filter to the chart list.
  // - CRM_RECOVERY: only show Cu (the only element stable enough in the current OREAS)
  // - BLANK: show all analytical blank charts
  const visibleCharts = useMemo(
    () => {
      let list = effectiveRefMaterial
        ? charts.filter((c) => c.reference_material === effectiveRefMaterial)
        : charts;

      const ct = filters.control_type;
      if (ct === 'BLANK') {
        list = list.filter((c) => c.control_type === 'BLANK' && c.element !== 'DENSITE');
      } else {
        // Default / CRM_RECOVERY: only Cu MRC charts
        list = list.filter(
          (c) => c.control_type === 'CRM_RECOVERY' && c.element === 'Cu',
        );
      }
      return list;
    },
    [charts, effectiveRefMaterial, filters.control_type],
  );

  // Keep the selection valid when the reference-material filter hides it.
  useEffect(() => {
    if (selectedChartId && !visibleCharts.find((c) => c.chart_id === selectedChartId)) {
      setSelectedChartId(visibleCharts.length > 0 ? visibleCharts[0].chart_id : null);
    }
  }, [visibleCharts, selectedChartId]);

  /**
   * Download the chart as a PNG image. The previous implementation cloned the
   * DOM (including the <canvas>), but a cloned canvas is blank — the bitmap is
   * not copied — so the downloaded file showed no chart. We grab the live
   * Chart.js canvas and export its bitmap directly (on a white background).
   */
  const handleDownload = () => {
    const grab = (): boolean => {
      const canvas = printRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
      if (!canvas) return false;

      const out = document.createElement('canvas');
      out.width = canvas.width;
      out.height = canvas.height;
      const ctx = out.getContext('2d');
      if (!ctx) return false;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(canvas, 0, 0);

      const safe = (s?: string | null) => (s || '').replace(/[^a-zA-Z0-9_-]+/g, '_');
      const name = `carte-qc-${safe(selectedChart?.element)}-${safe(selectedChart?.control_type)}${
        selectedChart?.reference_material ? '-' + safe(selectedChart.reference_material) : ''
      }.png`;

      const a = document.createElement('a');
      a.href = out.toDataURL('image/png');
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    };

    if (!grab()) {
      // Chart isn't mounted (e.g. the Violations tab is open) — switch to the
      // chart tab, then retry once it has rendered.
      setTab(0);
      setTimeout(grab, 500);
    }
  };

  const handlePdf = () => {
    // "Imprimer / PDF" — opens a print dialog scoped to the QC card. We inline
    // the live canvas as an image so the printout actually contains the chart.
    if (!printRef.current) return window.print();
    const printable = printRef.current.cloneNode(true) as HTMLElement;
    // Replace each (blank) cloned canvas with a snapshot <img> of the live one.
    const liveCanvases = printRef.current.querySelectorAll('canvas');
    const clonedCanvases = printable.querySelectorAll('canvas');
    clonedCanvases.forEach((cloned, i) => {
      const live = liveCanvases[i] as HTMLCanvasElement | undefined;
      if (!live) return;
      const img = document.createElement('img');
      img.src = live.toDataURL('image/png');
      img.style.maxWidth = '100%';
      cloned.replaceWith(img);
    });
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>Carte QA/QC - ${selectedChart?.element || ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; }
            h1 { font-size: 18px; margin-bottom: 8px; }
            .kpis { display: flex; gap: 16px; margin-bottom: 16px; }
            .kpi { background:#f5f5f5; padding:8px 12px; border-radius:4px; }
            .kpi small { color:#666; display:block; }
            canvas { max-width: 100% !important; height: auto !important; }
            table { width:100%; border-collapse: collapse; font-size: 12px; }
            th, td { border:1px solid #ddd; padding:4px 6px; text-align:left; }
            th { background:#fafafa; }
          </style>
        </head>
        <body>${printable.innerHTML}</body>
      </html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const renderChart = () => {
    if (!selectedChart) return null;
    const isPrepBlank =
      selectedChart.control_type === 'PREP_CONCASSAGE_BLANK' ||
      selectedChart.control_type === 'PREP_BROYAGE_BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_QUALITY' ||
      selectedChart.control_type === 'PREP_BROYAGE_QUALITY';
    const isCrm = selectedChart.control_type === 'CRM_RECOVERY';
    const certifiedValue = isCrm ? (selectedChart.certified_value ?? null) : null;
    const certifiedUnit = isCrm ? (selectedChart.certified_unit ?? null) : null;
    const yLabel = isPrepBlank
      ? 'Perte de masse (%)'
      : isCrm && certifiedValue
      ? `${selectedChart.element} (${certifiedUnit ?? 'valeur mesurée'})`
      : `${selectedChart.element} ${
          selectedChart.control_type === 'DUPLICATE_RPD'
            ? '(RPD %)'
            : selectedChart.control_type === 'BLANK'
            ? '(valeur blanc)'
            : '(Récupération %)'
        }`;
    if (selectedChart.control_type === 'DUPLICATE_RPD') {
      return <RpdChart summary={selectedChart} points={points} />;
    }
    if (
      selectedChart.control_type === 'BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_BLANK' ||
      selectedChart.control_type === 'PREP_BROYAGE_BLANK' ||
      selectedChart.control_type === 'PREP_CONCASSAGE_QUALITY' ||
      selectedChart.control_type === 'PREP_BROYAGE_QUALITY'
    ) {
      return <BlankChart summary={selectedChart} points={points} yLabel={yLabel} />;
    }
    return (
      <LeveyJenningsChart
        summary={selectedChart}
        points={points}
        yLabel={yLabel}
        certifiedValue={certifiedValue}
        certifiedUnit={certifiedUnit}
      />
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ShowChartIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Cartes de contrôle QA/QC
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Suivi statistique de la qualité (cartes de Levey-Jennings, règles de
        Westgard) pour vos échantillons : blancs analytiques, duplicatas, MRC,
        ainsi que les blancs de préparation mécanique (concassage, broyage).
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
        Affichage par défaut : 90 derniers jours. Modifiez la plage de dates pour voir l'historique complet.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Élément"
              placeholder="Cu, Au, ..."
              value={filters.element || ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, element: e.target.value || undefined }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Type de contrôle</InputLabel>
              <Select
                label="Type de contrôle"
                value={filters.control_type || ''}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, control_type: (e.target.value as any) || undefined }))
                }
              >
                {CONTROL_TYPES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              size="small"
              fullWidth
              disabled={!oreasApplicable || referenceMaterials.length === 0}
            >
              <InputLabel>MRC / OREAS</InputLabel>
              <Select
                label="MRC / OREAS"
                value={effectiveRefMaterial}
                onChange={(e) => setRefMaterial(e.target.value)}
              >
                <MenuItem value="">Tous les MRC</MenuItem>
                {referenceMaterials.map((rm) => (
                  <MenuItem key={rm} value={rm}>
                    {rm}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Du"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.from || defaultFrom}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || defaultFrom }))}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Au"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.to || ''}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '60vh', overflow: 'auto' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {visibleCharts.length} carte{visibleCharts.length > 1 ? 's' : ''}
                {refMaterial ? ` · ${refMaterial}` : ''}
              </Typography>
            </Box>
            {loadingCharts ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : visibleCharts.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée QA/QC pour vos échantillons sur cette période.
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {visibleCharts.map((c) => {
                  const inControl = (c.in_control_percentage ?? 100) >= 95;
                  return (
                    <Box
                      key={c.chart_id}
                      onClick={() => setSelectedChartId(c.chart_id)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: selectedChartId === c.chart_id ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {prettyChartLabel(c)}
                        </Typography>
                        {inControl ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <WarningIcon fontSize="small" color="warning" />
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {prettyLab(c.laboratory)} · {c.total_points} pts
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          {!selectedChart ? (
            <Paper
              sx={{
                p: 4,
                height: '60vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">
                Sélectionnez une carte dans la liste à gauche.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2} ref={printRef}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography variant="h6">
                          {prettyChartLabel(selectedChart)}
                          {selectedChart.reference_material && selectedChart.control_type === 'CRM_RECOVERY'
                            ? ` (${selectedChart.reference_material})`
                            : ''}
                        </Typography>
                        {(selectedChart.in_control_percentage ?? 100) >= 95 ? (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            label="En contrôle"
                          />
                        ) : (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<WarningIcon />}
                            label="À surveiller"
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {prettyLab(selectedChart.laboratory)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                      >
                        Télécharger
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PdfIcon />}
                        onClick={handlePdf}
                      >
                        PDF
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={2} sx={{ mt: 1 }} className="kpis">
                    <Grid item xs={6} md={3}>
                      <Box className="kpi">
                        <Typography variant="caption" color="text.secondary">
                          Moyenne
                        </Typography>
                        <Typography variant="h6">
                          {Number(selectedChart.baseline_mean).toFixed(4)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box className="kpi">
                        <Typography variant="caption" color="text.secondary">
                          ±1σ
                        </Typography>
                        <Typography variant="h6">
                          {Number(selectedChart.baseline_std_dev).toFixed(4)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box className="kpi">
                        <Typography variant="caption" color="text.secondary">
                          Total points
                        </Typography>
                        <Typography variant="h6">{selectedChart.total_points}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box className="kpi">
                        <Typography variant="caption" color="text.secondary">
                          % en contrôle
                        </Typography>
                        <Typography variant="h6">
                          {Number(selectedChart.in_control_percentage).toFixed(0)} %
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Paper sx={{ p: 2 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Carte" />
                  <Tab label="Violations" />
                </Tabs>
                {loadingDetail ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : tab === 0 ? (
                  renderChart()
                ) : (
                  <WestgardViolationsPanel points={points} />
                )}
              </Paper>
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientQcTab;
